
import {
  defineComponent,
  ref,
  computed,
  watch,
  onBeforeUnmount,
  nextTick,
  PropType,
} from 'vue'
import StarContentAudio from '@/components/star/StarContentAudio.vue'
import starZoneApi from '@/api/starZoneApi'
import { formatStarTime } from '@/utils/starZoneHelper'
import { jumpToStockChart } from '@/utils/stock'
import ws from '@/websocket/index.js'
import {
  ArticleStockItem,
  bindArticleImageClick,
  bindArticleQuoteClick,
  formatArticleHtmlContent,
  formatArticleRate,
  ARTICLE_BODY_WIDTH,
  initArticleVideos,
  initShareAudioInContainer,
  parseAttributeStockList,
  pauseOtherMedia,
  updateArticleStockRates,
} from '@/utils/articleContentHelper'

export default defineComponent({
  name: 'StarArticleContent',
  components: { StarContentAudio },
  props: {
    article: {
      type: Object as PropType<Record<string, any>>,
      default: () => ({}),
    },
    isWebSocket: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const contentRef = ref<HTMLElement | null>(null)
    const contentWidth = ref(ARTICLE_BODY_WIDTH)
    const video = ref<{ url?: string; coverURL?: string }>({})
    const stockList = ref<ArticleStockItem[]>([])

    let cleanupQuote: (() => void) | undefined
    let cleanupImage: (() => void) | undefined

    const attribute = computed(() => props.article?.attribute || {})
    const isNormalArticle = computed(() => !!props.article?.attribute || !!props.article?.content)
    const showTimeText = computed(() =>
      formatStarTime(props.article?.showTime || props.article?.publishTime)
    )
    const authorName = computed(() => {
      const author = props.article?.author || {}
      if (author.status === 1 && author.realName) return author.realName
      return author.realName || author.name || ''
    })
    const authorLogo = computed(() => {
      const author = props.article?.author || {}
      return author.status === 1 && author.logo ? author.logo : ''
    })
    const pdfUrl = computed(() => attribute.value.pdfUrl || '')
    const pdfName = computed(() => attribute.value.pdfName || 'article.pdf')
    const audioUrl = computed(() => attribute.value.articleAudio || '')
    const plainContent = computed(() => props.article?.content || '')

    const htmlContent = computed(() =>
      formatArticleHtmlContent(props.article?.content || '', contentWidth.value)
    )

    const syncContentWidth = () => {
      contentWidth.value = contentRef.value?.clientWidth || ARTICLE_BODY_WIDTH
    }

    const displayStockList = computed(() =>
      stockList.value.filter((item) => item.stockName)
    )

    const syncStockList = () => {
      stockList.value = parseAttributeStockList(attribute.value.stockList)
    }

    const onStockClick = (stock: ArticleStockItem) => {
      if (!stock.stockCode || !stock.stockMarket) return
      jumpToStockChart(`${stock.stockCode}.${stock.stockMarket}`)
    }

    const handleStatistics = (data: any) => {
      const preClosePrice = data?.QuoteData?.StatisticsData?.[0]?.PreClosePrice
      const inst = data?.Inst || ''
      const market = data?.Market || ''
      if (preClosePrice) {
        stockList.value = updateArticleStockRates(stockList.value, preClosePrice, inst, 0, market)
      }
    }

    const handleDyna = (data: any) => {
      const lastPrice = data?.QuoteData?.DynaData?.[0]?.LastPrice
      const inst = data?.Inst || ''
      const market = data?.Market || ''
      if (lastPrice) {
        stockList.value = updateArticleStockRates(stockList.value, lastPrice, inst, 1, market)
      }
    }

    const bindStockQuotes = () => {
      const socket = ws.websocket as any
      if (!props.isWebSocket || !socket) return
      socket.removeListener?.('onSTATISTICS', handleStatistics)
      socket.removeListener?.('onDYNA', handleDyna)
      if (!displayStockList.value.length) return
      socket.on('onSTATISTICS', handleStatistics)
      socket.on('onDYNA', handleDyna)
      subscribeStockData()
    }

    const subscribeStockData = () => {
      const socket = ws.websocket as any
      if (!props.isWebSocket || !socket) return
      unsubscribeStockData()
      stockList.value.forEach((item) => {
        const market = (item.stockMarket || '').toUpperCase()
        if (!item.stockCode || !market) return
        socket.safeSubscribeSTATISTICS(item.stockCode, market)
        socket.safeSubscribeDYNA(item.stockCode, market)
      })
    }

    const unsubscribeStockData = () => {
      const socket = ws.websocket as any
      if (!socket) return
      stockList.value.forEach((item) => {
        const market = (item.stockMarket || '').toUpperCase()
        if (!item.stockCode || !market) return
        socket.safeUnsubscribeSTATISTICS(item.stockCode, market)
        socket.safeUnsubscribeDYNA(item.stockCode, market)
      })
    }

    const fetchArticleVideo = async () => {
      if (!attribute.value.articleVideo || !props.article?.newsId) {
        video.value = {}
        return
      }
      try {
        const res = await starZoneApi.getArticleVideo({
          data: { newsId: props.article.newsId },
        })
        if (res?.code === 1 || res?.code === 0) {
          video.value = res.data || {}
        }
      } catch (e) {
        console.error('getArticleVideo failed', e)
      }
    }

    const enhanceContentDom = () => {
      cleanupQuote?.()
      cleanupImage?.()
      const root = contentRef.value
      initShareAudioInContainer(root)
      initArticleVideos(root)
      pauseOtherMedia(root?.closest('.article-content-container') as HTMLElement || root)
      cleanupQuote = bindArticleQuoteClick(root)
      cleanupImage = bindArticleImageClick(root)

      const imgLoadings = root?.querySelectorAll('.img_loading') || []
      imgLoadings.forEach((node) => node.removeAttribute('data-src'))
    }

    const setupArticle = async () => {
      syncStockList()
      await fetchArticleVideo()
      await nextTick()
      syncContentWidth()
      enhanceContentDom()
      bindStockQuotes()
    }

    watch(
      () => props.article?.newsId,
      () => {
        unsubscribeStockData()
        setupArticle()
      },
      { immediate: true }
    )

    onBeforeUnmount(() => {
      cleanupQuote?.()
      cleanupImage?.()
      unsubscribeStockData()
      const socket = ws.websocket as any
      socket?.removeListener('onSTATISTICS', handleStatistics)
      socket?.removeListener('onDYNA', handleDyna)
    })

    return {
      contentRef,
      video,
      isNormalArticle,
      showTimeText,
      authorName,
      authorLogo,
      pdfUrl,
      pdfName,
      audioUrl,
      plainContent,
      htmlContent,
      displayStockList,
      formatArticleRate,
      onStockClick,
    }
  },
})
